/**
 * PDV.js — Módulo de Ponto de Venda (Caixa)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 * Data: 27/05/2026
 *
 * Descrição: Interface completa do caixa para cobrança de tickets de estacionamento.
 * Fluxo: Busca por placa → Cálculo de tarifa → Aplicação de convênio → Pagamento.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import { SystemHealthCompact } from '../components/SystemHealthBadge';
import PlacaVeicular from '../components/PlacaVeicular';
import './PDV.css';

// Abas do PDV
const TABS = { CAIXA: 'caixa', AUDITORIA: 'auditoria' };

// Rótulos legíveis para ações de auditoria
const AUDIT_ACTION_LABELS = {
  pagamento_confirmado:    'Pagamento',
  pagamento_estornado:     'Estorno de pagamento',
  isencao_manual:          'Isenção Manual',
  sangria_caixa:           'Sangria',
  suprimento_caixa:        'Suprimento',
  abertura_manual_cancela: 'Cancela Manual',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  return `${h}h ${m}min`;
}

function formatDateTime(dt) {
  if (!dt) return '--';
  return new Date(dt).toLocaleString('pt-BR');
}

// Onda 99b — o ticket lido pelo QR que já não está em aberto.
//
// Era a faixa vermelha de erro. Não é erro: o código foi lido, o ticket foi
// achado, e o que o operador precisa é ver o desfecho — pago a tal hora — para
// dizer ao cliente. Verde quando o desfecho foi pagamento ou liberação; âmbar
// quando foi cancelamento ou encerramento por permanência, casos em que talvez
// seja preciso olhar de novo. O app da guarita desenha o mesmo cartão.
const FORMAS_DO_CARTAO = {
  dinheiro: 'Dinheiro', credito: 'Crédito', debito: 'Débito', pix: 'PIX',
  sem_parar: 'Sem Parar', isento: 'Isento', auto_dinheiro: 'Baixa automática',
};

function ClosedTicketCard({ ticket, onClose }) {
  const resolvido = ['pago', 'pago_automatico', 'isento'].includes(ticket.status)
    || (ticket.status === 'finalizado' && ticket.exit_time);
  const codigo = ticket.public_code ? `${ticket.public_code.slice(0, 4)} ${ticket.public_code.slice(4)}` : null;
  const forma = FORMAS_DO_CARTAO[ticket.payment_method];
  const linhas = [
    ticket.entry_time && ['Entrada', formatDateTime(ticket.entry_time)],
    ticket.paid_at && ['Pagamento', formatDateTime(ticket.paid_at)],
    ticket.exit_time && ['Saída', formatDateTime(ticket.exit_time)],
    ticket.amount_paid > 0 && ['Valor pago', `${formatCurrency(ticket.amount_paid)}${forma ? ` · ${forma}` : ''}`],
  ].filter(Boolean);

  return (
    <div className={`pdv-encerrado ${resolvido ? 'pdv-encerrado-ok' : 'pdv-encerrado-atencao'}`} role="status">
      <div className="pdv-encerrado-titulo">{ticket.title || 'Ticket encerrado'}</div>
      <div className="pdv-encerrado-cabeca">
        <span className="pdv-ticket-plate">{ticket.plate}</span>
        <span className="pdv-encerrado-id">Ticket #{ticket.id}{codigo ? ` · ${codigo}` : ''}</span>
      </div>
      {linhas.length > 0 && (
        <dl className="pdv-encerrado-linhas">
          {linhas.map(([rotulo, texto]) => (
            <div key={rotulo}><dt>{rotulo}</dt><dd>{texto}</dd></div>
          ))}
        </dl>
      )}
      <p className="pdv-encerrado-nota">
        {resolvido
          ? 'Nada a cobrar neste ticket.'
          : 'Este ticket não pode ser cobrado. Se o veículo ainda está no pátio, busque pela placa.'}
      </p>
      <button type="button" className="pdv-btn-secondary pdv-encerrado-fechar" onClick={onClose}>
        Próximo atendimento
      </button>
    </div>
  );
}

// Onda 69: o emoji saiu e a tecla de atalho entrou no lugar dele.
//
// Crédito e débito usavam o MESMO emoji de cartão, então o ícone não
// distinguia as duas formas — o operador tinha de ler o rótulo de qualquer
// jeito. Emoji ainda tem desenho e cor próprios, que mudam com o sistema
// operacional e não obedecem à paleta.
//
// A tecla ocupa o mesmo lugar e carrega informação de verdade: mostra o atalho
// enquanto ensina que ele existe. Num caixa com fila, cada troca de mão entre
// teclado e mouse custa segundos.
const PAYMENT_METHODS = [
  { value: 'dinheiro',   label: 'Dinheiro',       tecla: '1' },
  { value: 'credito',    label: 'Cartão Crédito', tecla: '2' },
  { value: 'debito',     label: 'Cartão Débito',  tecla: '3' },
  { value: 'pix',        label: 'PIX',            tecla: '4' },
  { value: 'sem_parar',  label: 'Sem Parar',      tecla: '5' },
];

/**
 * Cédulas com que o cliente costuma pagar, para o cálculo do troco.
 *
 * Onda 69: os atalhos eram calculados com `Math.ceil(valor / nota) * nota`, e
 * para uma tarifa de R$ 18,00 os botões de 5, 10 e 20 produziam todos R$ 20 —
 * três botões idênticos lado a lado, e nenhuma opção para quem entregasse
 * R$ 50. Agora são as cédulas reais em circulação, só as que cobrem a conta,
 * mais o valor exato.
 */
const CEDULAS = [2, 5, 10, 20, 50, 100, 200];

/**
 * Tipos de veículo que saem sem pagar permanência.
 *
 * Onda 71: o operador precisa saber disso ANTES de clicar.
 *
 * A grade do pátio mostrava todos os veículos iguais, e só depois de selecionar
 * um mensalista é que a tela dizia que não havia o que cobrar. Em toda a
 * história do G2 são 52 tickets de mensalista e credenciado, e **nenhum** com
 * cobrança — o operador estava selecionando para nada, com o cliente esperando.
 *
 * O rótulo diz o que ele precisa fazer, não só o que o veículo é.
 */
const SEM_COBRANCA = {
  mensalista:  { rotulo: 'Mensalista',  acao: 'não cobrar' },
  credenciado: { rotulo: 'Credenciado', acao: 'não cobrar' },
  isento:      { rotulo: 'Isento',      acao: 'não cobrar' },
};

function sugestoesDeTroco(valorDevido) {
  const uteis = CEDULAS.filter(c => c >= valorDevido);
  // Combinações de duas cédulas iguais cobrem o caso comum de quem paga
  // R$ 40 numa conta de R$ 35 com duas notas de 20.
  const dobras = CEDULAS.filter(c => c * 2 >= valorDevido && c < valorDevido).map(c => c * 2);
  const todas = [...new Set([...dobras, ...uteis])].sort((a, b) => a - b);
  return todas.slice(0, 4);
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function PDV() {
  const { token, can } = useAuth();
  // Estorno e auditoria do caixa são do administrador; o catálogo decide.
  const isAdmin = can('pdv_reverse');
  // Onda 68: isentar ganhou permissão própria. O botão some para quem não pode,
  // em vez de aparecer e devolver 403 com o cliente esperando no caixa.
  const podeIsentar = can('pdv_exempt');
  const [activeTab, setActiveTab] = useState(TABS.CAIXA);

  // Auditoria (apenas admin)
  const [auditLogs, setAuditLogs]       = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter]   = useState({ action: '', start_date: '', end_date: '' });

  // Sessão de caixa
  const [session, setSession]           = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [openingBalance, setOpeningBalance] = useState('');
  // Sangria / Suprimento
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [movementType, setMovementType]   = useState('sangria');
  const [movementAmount, setMovementAmount] = useState('');
  const [movementReason, setMovementReason] = useState('');
  const [savingMovement, setSavingMovement] = useState(false);
  const [movements, setMovements]         = useState([]);
  // Modal de fechamento de caixa
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closingBalance, setClosingBalance] = useState('');
  const [closingNotes, setClosingNotes]   = useState('');
  const [closingReport, setClosingReport] = useState(null);

  // Busca
  const [searchPlate, setSearchPlate]   = useState('');
  const [searching, setSearching]       = useState(false);
  const [searchError, setSearchError]   = useState('');
  // Onda 99b — ticket achado pelo código que já não está ativo. Não é erro: é a
  // resposta, e ganha um cartão em vez da faixa vermelha.
  const [closedTicket, setClosedTicket] = useState(null);

  // Ticket encontrado
  const [ticketData, setTicketData]     = useState(null); // { ticket, duration_minutes, amount_due, ... }
  const [evidenceModal, setEvidenceModal] = useState(null); // URL da imagem em tela cheia

  // Helper: monta URL do endpoint de evidência com token
  function evidenceUrl(imagePath) {
    if (!imagePath) return null;
    const token = localStorage.getItem('token');
    return `/api/evidence/${imagePath}?token=${encodeURIComponent(token)}`;
  }

  // Convênio
  const [agreements, setAgreements]     = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [applyingAgreement, setApplyingAgreement] = useState(false);
  const [agreementResult, setAgreementResult] = useState(null);

  // Pagamento
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [amountPaid, setAmountPaid]     = useState('');
  const [paying, setPaying]             = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [reversingPayment, setReversingPayment] = useState(false);
  // Modal de confirmação Sem Parar
  const [showSemPararModal, setShowSemPararModal] = useState(false);

  // Veículos no pátio (painel de atendimento rápido)
  const [yardVehicles, setYardVehicles]   = useState([]);
  const [yardLoading, setYardLoading]     = useState(false);
  const [yardSort, setYardSort]           = useState('entry'); // 'entry' | 'plate'
  // Onda 68: a lista mostrava 71 veículos, 21 deles registrados havia mais de
  // 24 horas num pátio cuja permanência média é de 2h14. São tickets abertos
  // por carros que só passaram na avenida, e ficam na lista até a janela de
  // abandono de 72 h expirar. O operador precisa distinguir os dois grupos.
  const [yardResumo, setYardResumo]       = useState(null);
  const [yardMostrarParados, setYardMostrarParados] = useState(false);
  const yardIntervalRef = useRef(null);

  const plateInputRef = useRef(null);

  // ─── Carregar sessão de caixa e convênios ──────────────────────────────────

  const loadYardVehicles = useCallback(async () => {
    setYardLoading(true);
    try {
      const resp = await api.get('/api/reports/yard?limit=200', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // /api/reports/yard retorna { total, vehicles: [...] }
      const list = resp.data?.vehicles || resp.data?.data || (Array.isArray(resp.data) ? resp.data : []);
      setYardVehicles(list);
      setYardResumo({
        stale_hours: resp.data?.stale_hours ?? 12,
        stale: resp.data?.stale ?? 0,
        recentes: resp.data?.recentes ?? list.length,
        duplicadas: resp.data?.duplicadas ?? 0,
      });
    } catch {
      setYardVehicles([]);
      setYardResumo(null);
    } finally {
      setYardLoading(false);
    }
  }, [token]);

  // Clique em veículo do pátio: preenche a placa e dispara a busca
  async function handleYardVehicleClick(plate) {
    setSearchPlate(plate);
    setSearching(true);
    setSearchError('');
    setClosedTicket(null);
    setTicketData(null);
    setAgreementResult(null);
    setPaymentResult(null);
    setSelectedAgreement('');
    setValidationCode('');
    try {
      const resp = await api.get(`/api/pdv/search?plate=${plate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketData(resp.data);
      setAmountPaid(resp.data.amount_final?.toFixed(2) || '0.00');
    } catch (e) {
      setSearchError(e.response?.data?.error || 'Erro ao buscar placa');
    } finally {
      setSearching(false);
    }
  }

  const loadSession = useCallback(async () => {
    setLoadingSession(true);
    try {
      const resp = await api.get('/api/pdv/cashier-session/current', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(resp.data);
    } catch {
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  }, [token]);

  const loadAgreements = useCallback(async () => {
    try {
      const resp = await api.get('/api/agreements', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      setAgreements(rows.filter(a => a.active));
    } catch {
      setAgreements([]);
    }
  }, [token]);

  useEffect(() => {
    loadSession();
    loadAgreements();
    loadYardVehicles();
    // Atualizar a lista do pátio a cada 60 segundos
    yardIntervalRef.current = setInterval(loadYardVehicles, 60000);
    return () => clearInterval(yardIntervalRef.current);
  }, [loadSession, loadAgreements, loadYardVehicles]);

  async function loadAuditLogs() {
    if (!isAdmin) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (auditFilter.action)     params.append('action',     auditFilter.action);
      if (auditFilter.start_date) params.append('start_date', auditFilter.start_date);
      if (auditFilter.end_date)   params.append('end_date',   auditFilter.end_date);
      const resp = await api.get(`/api/pdv/audit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(resp.data || []);
    } catch {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  }

  // ─── Sessão de Caixa ───────────────────────────────────────────────────────

  async function handleOpenSession() {
    try {
      const resp = await api.post('/api/pdv/cashier-session/open',
        { opening_balance: parseFloat(openingBalance) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        await loadSession();
        setOpeningBalance('');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao abrir caixa');
    }
  }

  async function handleCloseSession() {
    // Carregar movimentações da sessão para exibir no modal
    try {
      const resp = await api.get(`/api/pdv/cashier-session/${session.id}/movements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMovements(resp.data || []);
    } catch { setMovements([]); }
    setClosingBalance('');
    setClosingNotes('');
    setClosingReport(null);
    setShowCloseModal(true);
  }

  async function handleConfirmClose() {
    // Onda 68: o valor contado passou a ser obrigatório, aqui e no servidor.
    //
    // Ele era opcional dos dois lados, e quem deixasse em branco tinha o próprio
    // saldo esperado gravado no lugar: a diferença dava zero e a sessão ficava
    // registrada como conferida e exata. Uma conferência que não aconteceu era
    // indistinguível de um fechamento correto.
    if (closingBalance === '' || closingBalance === null) {
      toast.error('Informe o valor contado na gaveta. É o que permite apurar sobra ou falta.');
      return;
    }
    const contado = parseFloat(closingBalance);
    if (!Number.isFinite(contado) || contado < 0) {
      toast.error('O valor contado precisa ser um número igual ou maior que zero.');
      return;
    }
    try {
      const resp = await api.post('/api/pdv/cashier-session/close',
        {
          session_id:      session.id,
          closing_balance: contado,
          notes:           closingNotes || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        setClosingReport(resp.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao fechar caixa');
    }
  }

  function handleFinishClose() {
    setShowCloseModal(false);
    setClosingReport(null);
    setSession(null);
    resetAll();
  }

  // ─── Sangria / Suprimento ──────────────────────────────────────────────────

  function openMovementModal(type) {
    setMovementType(type);
    setMovementAmount('');
    setMovementReason('');
    setShowMovementModal(true);
  }

  async function handleSaveMovement() {
    if (!movementAmount || parseFloat(movementAmount) <= 0) {
      alert('Informe um valor válido');
      return;
    }
    if (!movementReason || movementReason.trim().length < 3) {
      alert('Informe o motivo (mínimo 3 caracteres)');
      return;
    }
    setSavingMovement(true);
    try {
      const resp = await api.post('/api/pdv/cashier-session/movement',
        {
          session_id: session.id,
          type:       movementType,
          amount:     parseFloat(movementAmount),
          reason:     movementReason.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        setShowMovementModal(false);
        // Atualizar totais da sessão
        await loadSession();
        alert(`${movementType === 'sangria' ? 'Sangria' : 'Suprimento'} de ${formatCurrency(parseFloat(movementAmount))} registrado com sucesso!`);
      }
    } catch (e) {
      alert(e.response?.data?.error || `Erro ao registrar ${movementType}`);
    } finally {
      setSavingMovement(false);
    }
  }

  // ─── Busca por Placa ───────────────────────────────────────────────────────

  function handlePlateInput(e) {
    setSearchPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchPlate || searchPlate.length < 7) {
      setSearchError('Informe a placa completa (7 caracteres) ou o código do ticket (8)');
      return;
    }
    // Onda 99 — o mesmo campo recebe a placa ou o código do ticket. Um leitor de
    // QR de balcão é um teclado: digita os oito caracteres e aperta Enter, e cai
    // aqui. Sete caracteres seguem pelo caminho de sempre (?plate=), intocado;
    // só os oito — que nunca foram placa — vão por ?q=, e o servidor decide.
    const consulta = searchPlate.length === 8 ? `q=${searchPlate}` : `plate=${searchPlate}`;
    setClosedTicket(null);
    setSearching(true);
    setSearchError('');
    setTicketData(null);
    setAgreementResult(null);
    setPaymentResult(null);
    setSelectedAgreement('');
    setValidationCode('');
    try {
      const resp = await api.get(`/api/pdv/search?${consulta}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketData(resp.data);
      setAmountPaid(resp.data.amount_final?.toFixed(2) || '0.00');
      // Achado pelo código, o campo passa a mostrar a PLACA: é por ela que o
      // operador confere o carro, e o código já cumpriu o papel dele.
      if (resp.data?.ticket?.plate) setSearchPlate(resp.data.ticket.plate);
    } catch (e) {
      const encerrado = e.response?.data?.closed_ticket;
      if (encerrado) { setClosedTicket(encerrado); setSearchPlate(''); }
      else setSearchError(e.response?.data?.error || 'Erro ao buscar o ticket');
    } finally {
      setSearching(false);
    }
  }

  // ─── Aplicar Convênio ──────────────────────────────────────────────────────

  async function handleApplyAgreement() {
    if (!selectedAgreement) return;
    setApplyingAgreement(true);
    try {
      const resp = await api.post('/api/pdv/apply-agreement',
        {
          ticket_id: ticketData.ticket.id,
          agreement_id: parseInt(selectedAgreement),
          validation_code: validationCode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgreementResult(resp.data);
      setAmountPaid(resp.data.amount_final?.toFixed(2) || '0.00');
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao aplicar convênio');
    } finally {
      setApplyingAgreement(false);
    }
  }

  function handleRemoveAgreement() {
    setAgreementResult(null);
    setSelectedAgreement('');
    setValidationCode('');
    setAmountPaid(ticketData?.amount_due?.toFixed(2) || '0.00');
  }

  // ─── Onda 69: atalhos de teclado ───────────────────────────────────────────
  //
  // A tela não tinha um único manipulador de teclado. Escolher a forma de
  // pagamento, confirmar e avançar para o próximo atendimento exigiam mouse, e
  // num caixa com fila cada troca de mão custa segundos.
  //
  // O cuidado que faz isso funcionar: placas brasileiras têm números, e o campo
  // de busca abre em foco. Capturar "1" globalmente quebraria a digitação. Por
  // isso, dentro de qualquer campo de texto só Enter e Esc valem — as teclas de
  // atalho só atuam quando o foco está fora de um campo, que é o estado da tela
  // depois que o ticket foi encontrado.
  const escolherForma = useCallback((valor) => {
    setPaymentMethod(valor);
    // Para as formas que não têm troco, o valor recebido é o devido. Isso já era
    // feito no clique; agora vale também para o atalho.
    if (valor !== 'dinheiro') {
      const devido = agreementResult ? agreementResult.amount_final : (ticketData?.amount_due || 0);
      setAmountPaid(Number(devido).toFixed(2));
    } else {
      setAmountPaid('');
    }
  }, [agreementResult, ticketData]);

  useEffect(() => {
    function aoTeclar(e) {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const alvo = e.target;
      const emCampo = alvo && /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName);
      // Com um modal aberto, o teclado pertence a ele: confirmar o pagamento
      // por baixo da conferência do Sem Parar seria pular justamente a
      // conferência.
      const modalAberto = showCloseModal || showMovementModal || showSemPararModal || evidenceModal;
      if (modalAberto) return;

      // Enter: confirma o pagamento, ou avança para o próximo atendimento.
      if (e.key === 'Enter') {
        if (paymentResult) { e.preventDefault(); resetAll(); return; }
        // No campo de placa, o Enter pertence ao formulário de busca.
        if (emCampo && alvo.classList?.contains('pdv-plate-input')) return;
        if (ticketData && !paying) { e.preventDefault(); handlePayClick(); }
        return;
      }

      // Esc: abandona o atendimento em curso e volta para a busca.
      if (e.key === 'Escape') {
        if (ticketData || paymentResult) { e.preventDefault(); resetAll(); }
        return;
      }

      if (emCampo) return;   // daqui para baixo, só fora de campos de texto

      const forma = PAYMENT_METHODS.find(m => m.tecla === e.key);
      if (forma && ticketData && !paymentResult) {
        e.preventDefault();
        escolherForma(forma.value);
      }
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  });

  // ─── Pagamento ─────────────────────────────────────────────────────────────

  // Confirmação Sem Parar: abre modal antes de processar
  function handlePayClick() {
    if (!ticketData) return;
    if (paymentMethod === 'sem_parar') {
      setShowSemPararModal(true);
      return;
    }
    handlePay();
  }

  async function handleConfirmSemParar() {
    setShowSemPararModal(false);
    await handlePay();
  }

  async function handlePay() {
    if (!ticketData) return;
    const amountDue = agreementResult ? agreementResult.amount_final : ticketData.amount_due;
    const paid = parseFloat(amountPaid) || 0;

    if (paymentMethod === 'dinheiro' && paid < amountDue) {
      alert(`Valor recebido (${formatCurrency(paid)}) menor que o valor devido (${formatCurrency(amountDue)})`);
      return;
    }

    // Sem Parar: não exige valor recebido manual — cobra exatamente o valor devido
    if (paymentMethod === 'sem_parar') {
      setAmountPaid(amountDue.toFixed(2));
    } else if (paymentMethod !== 'dinheiro') {
      if (!window.confirm(`Confirmar pagamento de ${formatCurrency(amountDue)} via ${PAYMENT_METHODS.find(m => m.value === paymentMethod)?.label}?`)) return;
    }

    setPaying(true);
    try {
      const resp = await api.post('/api/pdv/pay',
        {
          ticket_id: ticketData.ticket.id,
          payment_method: paymentMethod,
          amount_paid: paid,
          amount_due: amountDue,
          discount_amount: agreementResult?.discount_amount || 0,
          agreement_id: agreementResult ? parseInt(selectedAgreement) : null,
          cashier_session_id: session?.id || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentResult(resp.data);
      if (session) await loadSession();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao registrar pagamento');
    } finally {
      setPaying(false);
    }
  }

  async function handleExempt() {
    if (!ticketData) return;
    // Onda 68: o motivo passou a ser obrigatório, aqui e no servidor.
    //
    // A caixa de diálogo do navegador aceita texto vazio, e o servidor não
    // exigia nada: bastava clicar OK para liberar a saída sem cobrar e sem
    // justificativa. Somado à auditoria quebrada, não sobrava rastro nenhum.
    // A caixa nativa some na Onda C; a validação fica desde já.
    const reason = window.prompt(
      `Isentar a placa ${ticketData.ticket.plate}?\n\n` +
      'Informe o motivo (mínimo de 5 caracteres). Ele fica registrado na auditoria.'
    );
    if (reason === null) return;
    if (String(reason).trim().length < 5) {
      toast.error('A isenção exige um motivo de ao menos 5 caracteres.');
      return;
    }
    try {
      const resp = await api.post('/api/pdv/exempt',
        { ticket_id: ticketData.ticket.id, reason: String(reason).trim(), cashier_session_id: session?.id || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        setPaymentResult({ ...resp.data, payment_method: 'isento', amount_paid: 0, change_amount: 0, amount_due: 0 });
        if (session) await loadSession();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao isentar ticket');
    }
  }

  function resetAll() {
    setSearchPlate('');
    setTicketData(null);
    setAgreementResult(null);
    setPaymentResult(null);
    setSelectedAgreement('');
    setValidationCode('');
    setAmountPaid('');
    setSearchError('');
    // Recarregar lista do pátio após pagamento
    loadYardVehicles();
    setTimeout(() => plateInputRef.current?.focus(), 100);
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loadingSession) {
    return <div className="pdv-loading"><div className="pdv-spinner" /><p>Carregando caixa...</p></div>;
  }

  // Tela de abertura de caixa
  if (!session) {
    return (
      <div className="pdv-open-session">
        <div className="pdv-open-card">
          <h2>Abrir Caixa</h2>
          {/* Onda 75: a tela dizia só "informe o saldo inicial", sem dizer o que
              contar. É o mesmo problema do fechamento, do outro lado do turno:
              cartão e PIX não passam pela gaveta, e o troco que já está nela é o
              que abre a conta do dia. Sem essa frase, quem abre com o troco na
              mão e digita zero cria uma sobra no fechamento. */}
          <p>Conte o dinheiro que já está na gaveta e informe abaixo. É a partir dele
          que o sistema apura sobra ou falta no fim do turno.</p>
          <div className="pdv-open-form">
            <label>Saldo Inicial (R$)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="0,00"
              value={openingBalance}
              onChange={e => setOpeningBalance(e.target.value)}
            />
            <small className="pdv-open-nota">
              Se a gaveta estiver vazia, informe zero. Dinheiro que chegar depois
              entra pelo botão Suprimento, com motivo registrado.
            </small>
            <button className="pdv-btn-primary btn-large" onClick={handleOpenSession}>
              Abrir Caixa
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tela de resultado de pagamento
  if (paymentResult) {
    const isExempt = paymentResult.payment_method === 'isento';
    const reversePayment = async () => {
      if (!isAdmin || !paymentResult.payment_id) return;
      const reason = window.prompt('Motivo do estorno (mínimo de 5 caracteres):');
      if (!reason || reason.trim().length < 5) return;
      setReversingPayment(true);
      try {
        await api.post(`/api/pdv/payments/${paymentResult.payment_id}/reverse`, {
          amount: paymentResult.amount_due,
          reason: reason.trim(),
          cashier_session_id: session?.id,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Estorno registrado e auditado');
        resetAll();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Não foi possível estornar o pagamento');
      } finally {
        setReversingPayment(false);
      }
    };
    return (
      <div className="pdv-result">
        <div className={`pdv-result-card ${isExempt ? 'exempt' : 'success'}`}>
          
          <h2>{isExempt ? 'Veículo Isento' : 'Pagamento Confirmado'}</h2>
          <div className="pdv-result-plate">
            <PlacaVeicular placa={paymentResult.plate} tamanho="g" />
          </div>
          <div className="pdv-result-details">
            <div className="pdv-result-row">
              <span>Permanência</span>
              <strong>{formatDuration(paymentResult.duration_minutes)}</strong>
            </div>
            {!isExempt && (
              <>
                <div className="pdv-result-row">
                  <span>Valor Cobrado</span>
                  <strong>{formatCurrency(paymentResult.amount_due)}</strong>
                </div>
                <div className="pdv-result-row">
                  <span>Forma de Pagamento</span>
                  <strong>{PAYMENT_METHODS.find(m => m.value === paymentResult.payment_method)?.label}</strong>
                </div>
                {paymentResult.payment_method === 'dinheiro' && (
                  <>
                    <div className="pdv-result-row">
                      <span>Valor Recebido</span>
                      <strong>{formatCurrency(paymentResult.amount_paid)}</strong>
                    </div>
                    <div className="pdv-result-row highlight">
                      <span>Troco</span>
                      <strong className="change">{formatCurrency(paymentResult.change_amount)}</strong>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* ─── Onda 73: a janela de saída no lugar do aviso de webhook ─────
              A tela dizia "Cancela acionada" ou "Webhook não configurado". As
              duas mensagens perderam o sentido quando o pagamento deixou de
              abrir a cancela: a liberação acontece na câmera de saída, e o
              acionamento virá do broker MQTT. Um aviso sobre um webhook que não
              deve disparar só assusta quem opera.

              No lugar entra o que o operador precisa DIZER ao cliente: até que
              horas ele pode cruzar a cancela. Era uma linha discreta no meio dos
              detalhes, com o rótulo "Tolerância de Saída" — vocabulário do
              sistema, não de quem atende. */}
          {paymentResult.tolerance_expires_at && !isExempt && (
            <div className="pdv-result-janela">
              <span className="pdv-result-janela-rotulo">Pode sair até</span>
              <strong className="pdv-result-janela-hora">
                {new Date(paymentResult.tolerance_expires_at)
                  .toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </strong>
              <span className="pdv-result-janela-nota">
                A cancela abre sozinha quando a câmera reconhecer a placa.
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {isAdmin && !isExempt && paymentResult.payment_id && (
              <button className="pdv-btn-secondary" onClick={reversePayment} disabled={reversingPayment}>
                {reversingPayment ? 'Estornando...' : 'Estornar pagamento'}
              </button>
            )}
            <button className="pdv-btn-primary btn-large" onClick={resetAll}>Próximo Atendimento</button>
          </div>
        </div>
      </div>
    );
  }

  // Onda 71: quantos veículos visíveis na grade não passam pelo caixa. Conta só
  // os que estão à mostra, para bater com o que o operador vê.
  const livresNoPatio = yardVehicles
    .filter(v => yardMostrarParados || !v.stale)
    .filter(v => SEM_COBRANCA[v.vehicle_type]).length;

  const currentAmountDue = agreementResult ? agreementResult.amount_final : (ticketData?.amount_due || 0);
  const changeAmount = Math.max(0, (parseFloat(amountPaid) || 0) - currentAmountDue);

  // ─── Render: Aba de Auditoria (apenas admin) ─────────────────────────────────
  if (activeTab === TABS.AUDITORIA && isAdmin) {
    return (
      <div className="pdv-container">
        <div className="pdv-header">
          <div className="pdv-header-left">
            <h1>Log de Auditoria</h1>
          </div>
          <div className="pdv-header-right">
            <button className="pdv-btn-secondary" onClick={() => setActiveTab(TABS.CAIXA)}>
              ← Voltar ao Caixa
            </button>
          </div>
        </div>
        <div className="pdv-audit-panel">
          <div className="pdv-audit-filters">
            <select
              value={auditFilter.action}
              onChange={e => setAuditFilter(f => ({ ...f, action: e.target.value }))}
              className="pdv-input"
            >
              <option value="">Todas as ações</option>
              {Object.entries(AUDIT_ACTION_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <input
              type="date"
              value={auditFilter.start_date}
              onChange={e => setAuditFilter(f => ({ ...f, start_date: e.target.value }))}
              className="pdv-input"
              title="Data inicial"
            />
            <input
              type="date"
              value={auditFilter.end_date}
              onChange={e => setAuditFilter(f => ({ ...f, end_date: e.target.value }))}
              className="pdv-input"
              title="Data final"
            />
            <button className="pdv-btn-primary" onClick={loadAuditLogs} disabled={auditLoading}>
              {auditLoading ? 'Buscando...' : 'Filtrar'}
            </button>
          </div>

          {auditLogs.length === 0 && !auditLoading && (
            <div className="pdv-audit-empty">
              <p>Nenhum registro encontrado. Aplique os filtros e clique em Filtrar.</p>
            </div>
          )}

          {auditLogs.length > 0 && (
            <div className="pdv-audit-table-wrapper">
              <table className="pdv-audit-table">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Operador</th>
                    <th>Ação</th>
                    <th>Entidade</th>
                    <th>Descrição</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className={`pdv-audit-row audit-${log.action}`}>
                      <td>{formatDateTime(log.created_at)}</td>
                      <td>{log.operator_name || '—'}</td>
                      <td>
                        <span className={`pdv-audit-badge pdv-audit-badge-${log.action}`}>
                          {AUDIT_ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td>{log.entity || '—'} {log.entity_id ? `#${log.entity_id}` : ''}</td>
                      <td className="pdv-audit-desc">{log.description || '—'}</td>
                      <td>{log.ip_address || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="pdv-container">
      {/* Header com info da sessão */}
      <div className="pdv-header">
        <div className="pdv-header-left">
          <h1>Caixa</h1>
          <span className="pdv-session-badge">Sessão #{session.id} aberta</span>
        </div>
        {/* Onda 69: o cabeçalho tinha título, número da sessão, cinco totais,
            dois botões de movimentação, auditoria, indicador de saúde e o botão
            de encerrar o turno, tudo na mesma faixa. Agora são três blocos com
            pesos diferentes: o que se lê, o que se usa às vezes, e o que encerra
            o expediente — este último separado, porque não se aperta por engano. */}
        <div className="pdv-session-totals">
          <div className="pdv-total-item">
            <span>Tickets</span>
            <strong>{session.total_tickets}</strong>
          </div>
          <div className="pdv-total-item">
            <span>Dinheiro</span>
            <strong>{formatCurrency(session.total_cash)}</strong>
          </div>
          <div className="pdv-total-item">
            <span>Crédito</span>
            <strong>{formatCurrency(session.total_credit)}</strong>
          </div>
          <div className="pdv-total-item">
            <span>Débito</span>
            <strong>{formatCurrency(session.total_debit)}</strong>
          </div>
          <div className="pdv-total-item">
            <span>PIX</span>
            <strong>{formatCurrency(session.total_pix)}</strong>
          </div>
          {parseFloat(session.total_sem_parar || 0) > 0 && (
            <div className="pdv-total-item">
              <span>Sem Parar</span>
              <strong>{formatCurrency(session.total_sem_parar)}</strong>
            </div>
          )}
        </div>

        <div className="pdv-header-acoes">
          <div className="pdv-acoes-rotina">
            <button className="pdv-btn-movement sangria" onClick={() => openMovementModal('sangria')} title="Registrar retirada de dinheiro do caixa">
              ↓ Sangria
            </button>
            <button className="pdv-btn-movement suprimento" onClick={() => openMovementModal('suprimento')} title="Adicionar dinheiro ao caixa">
              ↑ Suprimento
            </button>
            {isAdmin && (
              <button className="pdv-btn-audit" onClick={() => { setActiveTab(TABS.AUDITORIA); loadAuditLogs(); }} title="Log de auditoria de ações críticas">
                Auditoria
              </button>
            )}
          </div>
          <div className="pdv-acoes-turno">
            <SystemHealthCompact intervalMs={30000} />
            <button className="pdv-btn-close-session" onClick={handleCloseSession} title="Encerrar o turno e conferir a gaveta">
              Fechar Caixa
            </button>
          </div>
        </div>
      </div>
      {/* Onda 70: a tela deixou de ser duas colunas fixas.
          Sem atendimento em curso, a coluna da direita ficava VAZIA — metade da
          tela em branco ao lado de uma tabela espremida. Agora o pátio ocupa a
          largura toda enquanto o operador espera, e a tela se divide em duas
          só quando há um veículo a cobrar. */}
      <div className={`pdv-body${ticketData ? '' : ' pdv-body-espera'}`}>
        {/* Coluna Esquerda: Busca e Ticket */}
        <div className="pdv-left">
          {/* Busca por placa */}
          <div className="pdv-card">
            <h3>Buscar placa ou ticket</h3>
            <form onSubmit={handleSearch} className="pdv-search-form">
              <input
                ref={plateInputRef}
                type="text"
                className="pdv-plate-input"
                placeholder="ABC1D23"
                aria-label="Placa do veículo ou código do ticket"
                value={searchPlate}
                onChange={handlePlateInput}
                maxLength={8}
                autoFocus
              />
              <button type="submit" className="pdv-btn-primary" disabled={searching}>
                {searching ? '...' : 'Buscar'}
              </button>
            </form>
            <p className="pdv-search-dica">
              Digite a placa, ou leia o QR do ticket com o leitor — o código impresso sob o QR também serve.
            </p>
            {searchError && <div className="pdv-error">{searchError}</div>}
            {closedTicket && <ClosedTicketCard ticket={closedTicket} onClose={() => setClosedTicket(null)} />}
          </div>

          {/* Lista de veículos no pátio — visível apenas quando não há busca ativa */}
          {!ticketData && !paymentResult && (
            <div className="pdv-yard-panel">
              <div className="pdv-yard-header">
                <h3>🅿 Veículos no Pátio</h3>
                <div className="pdv-yard-controls">
                  <span className="pdv-yard-count">
                    {yardLoading
                      ? 'Atualizando...'
                      : yardResumo
                        ? `${yardResumo.recentes} no pátio`
                        : `${yardVehicles.length} veículo${yardVehicles.length !== 1 ? 's' : ''}`}
                  </span>
                  {/* Onda 71: quantos daqueles não passam pelo caixa. Sem esta
                      contagem, o operador olha a lista e supõe que todos são
                      cobrança pendente. */}
                  {!yardLoading && livresNoPatio > 0 && (
                    <span
                      className="pdv-yard-count-livres"
                      title="Mensalistas, credenciados e isentos: saem sem pagar permanência"
                    >
                      {livresNoPatio} sem cobrança
                    </span>
                  )}
                  {/* Onda 68: os registros antigos ficam recolhidos, e o operador
                      abre quando precisa. Antes eles se misturavam aos veículos
                      de fato presentes e tiravam a confiança da lista inteira. */}
                  {!yardLoading && yardResumo?.stale > 0 && (
                    <button
                      className={`pdv-yard-sort-btn ${yardMostrarParados ? 'active' : ''}`}
                      onClick={() => setYardMostrarParados(v => !v)}
                      title={`Registros com mais de ${yardResumo.stale_hours}h. Costumam ser tickets abertos por veículos que passaram na via, e não entraram.`}
                    >
                      {yardMostrarParados ? 'Ocultar' : 'Ver'} {yardResumo.stale} parado{yardResumo.stale !== 1 ? 's' : ''} há +{yardResumo.stale_hours}h
                    </button>
                  )}
                  <button
                    className={`pdv-yard-sort-btn ${yardSort === 'entry' ? 'active' : ''}`}
                    onClick={() => setYardSort('entry')}
                    title="Ordenar por hora de entrada"
                  >
                    Entrada
                  </button>
                  <button
                    className={`pdv-yard-sort-btn ${yardSort === 'plate' ? 'active' : ''}`}
                    onClick={() => setYardSort('plate')}
                    title="Ordenar por placa"
                  >
                    Placa
                  </button>
                  <button
                    className="pdv-yard-refresh-btn"
                    onClick={loadYardVehicles}
                    disabled={yardLoading}
                    title="Atualizar lista"
                  >
                    ↻
                  </button>
                </div>
              </div>

              {yardLoading && yardVehicles.length === 0 ? (
                <div className="pdv-yard-loading">Carregando veículos...</div>
              ) : yardVehicles.length === 0 ? (
                <div className="pdv-yard-empty">Nenhum veículo no pátio no momento.</div>
              ) : (
                <>
                  {/* Onda 70: a tabela de três colunas virou uma grade de
                      cartões, cada um com a placa desenhada como ela é no
                      para-brisa.

                      A tabela pedia leitura linha a linha para encontrar um
                      veículo; a placa desenhada é reconhecida de relance, que é
                      o que o operador faz de fato — ele olha o carro parado à
                      frente e procura aquela placa na tela. O modelo da placa,
                      Mercosul ou anterior, aparece sozinho no desenho. */}
                  <div className="pdv-yard-grid">
                    {[...yardVehicles]
                      .filter(v => yardMostrarParados || !v.stale)
                      .sort((a, b) => {
                        if (yardSort === 'plate') return a.plate.localeCompare(b.plate);
                        return new Date(b.entry_time) - new Date(a.entry_time);
                      })
                      .slice(0, 60)
                      .map(v => {
                        const entryMs = new Date(v.entry_time).getTime();
                        const mins = Math.floor((Date.now() - entryMs) / 60000);
                        const livre = SEM_COBRANCA[v.vehicle_type] || null;
                        return (
                          <button
                            key={v.ticket_id || v.id || `${v.plate}-${v.entry_time}`}
                            className={`pdv-yard-card${v.stale ? ' parado' : ''}${v.duplicate_plate ? ' duplicada' : ''}${livre ? ' livre' : ''}`}
                            onClick={() => handleYardVehicleClick(v.plate)}
                            title={livre
                              ? `${v.plate} é ${livre.rotulo.toLowerCase()}: sai sem pagar permanência.`
                              : v.stale
                                ? `${v.plate} está registrada há mais de ${yardResumo?.stale_hours || 12}h. Confirme no pátio antes de cobrar.`
                                : `Cobrar ${v.plate}`}
                          >
                            <PlacaVeicular placa={v.plate} tamanho="p" />
                            {/* Onda 71: para quem não paga permanência, a hierarquia
                                se inverte. O tempo no pátio é o que decide o valor
                                de um avulso, e não decide nada para um mensalista:
                                ali o que o operador precisa ler é que aquele
                                veículo não passa pelo caixa. */}
                            {livre ? (
                              <div className="pdv-yard-card-info">
                                <span className="pdv-yard-card-livre">{livre.rotulo}</span>
                                <span className="pdv-yard-card-hora">não passa pelo caixa</span>
                              </div>
                            ) : (
                              <div className="pdv-yard-card-info">
                                <span className="pdv-yard-card-tempo">{formatDuration(mins)}</span>
                                <span className="pdv-yard-card-hora">
                                  desde {new Date(v.entry_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            )}
                            {/* A placa com dois tickets ativos é entrada duplicada:
                                cobrar um deixa o outro aberto. */}
                            {v.duplicate_plate && (
                              <span className="pdv-yard-aviso" title="Esta placa tem mais de um ticket aberto">
                                2 tickets
                              </span>
                            )}
                          </button>
                        );
                      })
                    }
                  </div>
                  {yardVehicles.length > 60 && (
                    <div className="pdv-yard-more">
                      Exibindo 60 de {yardVehicles.length} veículos. Use a busca para localizar outros.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Dados do ticket */}
          {ticketData && (
            <div className="pdv-card pdv-ticket-card">
              <div className="pdv-ticket-header">
                {/* A placa em tamanho grande é o que o operador confere contra o
                    veículo parado à frente dele. Era um texto entre os demais. */}
                <PlacaVeicular placa={ticketData.ticket.plate} tamanho="g" />
                <div className={`pdv-ticket-type ${ticketData.vehicle_type}`}>
                  {ticketData.vehicle_type === 'mensalista' ? 'Mensalista' :
                   ticketData.vehicle_type === 'credenciado' ? 'Credenciado' :
                   ticketData.vehicle_type === 'isento' ? 'Isento' : 'Avulso'}
                </div>
              </div>

              {/* Evidência fotográfica da entrada */}
              <div className="pdv-evidence-row">
                {ticketData.ticket.entry_image_path ? (
                  <div
                    className="pdv-evidence-thumb"
                    title="Clique para ampliar a evidência fotográfica"
                    onClick={() => setEvidenceModal(evidenceUrl(ticketData.ticket.entry_image_path))}
                  >
                    <img
                      src={evidenceUrl(ticketData.ticket.entry_image_path)}
                      alt="Evidência de entrada"
                      onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                    <div className="pdv-evidence-error" style={{display:'none'}}>
                      <small>Evidência não disponível</small>
                    </div>
                    <div className="pdv-evidence-overlay">Ampliar</div>
                  </div>
                ) : (
                  <div className="pdv-evidence-placeholder">
                    <small>Sem evidência fotográfica</small>
                  </div>
                )}
              </div>
              <div className="pdv-ticket-info">
                <div className="pdv-info-row">
                  <span>Entrada</span>
                  <strong>{formatDateTime(ticketData.ticket.entry_time)}</strong>
                </div>
                <div className="pdv-info-row">
                  <span>Permanência</span>
                  <strong className="duration">{formatDuration(ticketData.duration_minutes)}</strong>
                </div>
                {ticketData.ticket.customer_name && (
                  <div className="pdv-info-row">
                    <span>Cliente</span>
                    <strong>{ticketData.ticket.customer_name}</strong>
                  </div>
                )}
              </div>

              {/* Breakdown de tarifa */}
              {!ticketData.is_exempt && ticketData.breakdown && (
                <div className="pdv-breakdown">
                  <div className="pdv-breakdown-title">Composição do Valor</div>
                  {ticketData.breakdown.in_grace_period ? (
                    <div className="pdv-breakdown-row free">Dentro da carência — Gratuito</div>
                  ) : (
                    <>
                      {ticketData.breakdown.full_days > 0 && (
                        <div className="pdv-breakdown-row">
                          {ticketData.breakdown.full_days}x Diária
                          <span>{formatCurrency(ticketData.breakdown.full_days * parseFloat(ticketData.price_table?.daily_max_price || 0))}</span>
                        </div>
                      )}
                      {ticketData.breakdown.first_period_applied && (
                        <div className="pdv-breakdown-row">
                          1º Período ({ticketData.price_table?.first_period_min}min)
                          <span>{formatCurrency(ticketData.price_table?.first_period_price)}</span>
                        </div>
                      )}
                      {ticketData.breakdown.extra_fractions > 0 && (
                        <div className="pdv-breakdown-row">
                          {ticketData.breakdown.extra_fractions}x Fração ({ticketData.price_table?.fraction_min}min)
                          <span>{formatCurrency(ticketData.breakdown.extra_fractions * parseFloat(ticketData.price_table?.fraction_price || 0))}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="pdv-breakdown-total">
                    <span>Total</span>
                    <strong>{formatCurrency(ticketData.amount_due)}</strong>
                  </div>
                </div>
              )}

              {ticketData.is_exempt && (
                <div className="pdv-exempt-badge">
                  Este veículo é isento de cobrança
                  <button className="pdv-btn-secondary pdv-btn-sm" onClick={handleExempt}>
                    Registrar Saída
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Coluna Direita: Convênio e Pagamento */}
        {ticketData && !ticketData.is_exempt && (
          <div className="pdv-right">
            {/* Convênio */}
            <div className="pdv-card">
              <h3>Convênio</h3>
              {!agreementResult ? (
                <div className="pdv-agreement-form">
                  <select
                    value={selectedAgreement}
                    onChange={e => setSelectedAgreement(e.target.value)}
                  >
                    <option value="">Sem convênio</option>
                    {agreements.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} — {
                          a.discount_type === 'percentual' ? `${a.discount_value}%` :
                          a.discount_type === 'valor' ? formatCurrency(a.discount_value) :
                          a.discount_type === 'tempo' ? `${a.discount_value}h grátis` :
                          'Isenção total'
                        }
                      </option>
                    ))}
                  </select>
                  {selectedAgreement && (
                    <input
                      type="text"
                      placeholder="Código de validação (se necessário)"
                      value={validationCode}
                      onChange={e => setValidationCode(e.target.value)}
                    />
                  )}
                  <button
                    className="pdv-btn-secondary"
                    onClick={handleApplyAgreement}
                    disabled={!selectedAgreement || applyingAgreement}
                  >
                    {applyingAgreement ? 'Aplicando...' : 'Aplicar Convênio'}
                  </button>
                </div>
              ) : (
                <div className="pdv-agreement-applied">
                  <div className="pdv-agreement-name">{agreementResult.agreement.name}</div>
                  <div className="pdv-agreement-detail">
                    <span>Desconto</span>
                    <strong className="discount">- {formatCurrency(agreementResult.discount_amount)}</strong>
                  </div>
                  <div className="pdv-agreement-detail">
                    <span>Valor Final</span>
                    <strong>{formatCurrency(agreementResult.amount_final)}</strong>
                  </div>
                  <button className="pdv-btn-link" onClick={handleRemoveAgreement}>
                    Remover convênio
                  </button>
                </div>
              )}
            </div>

            {/* Pagamento */}
            <div className="pdv-card pdv-payment-card">
              <h3>Pagamento</h3>

              <div className="pdv-amount-due">
                <span>Valor a Cobrar</span>
                <strong className="pdv-amount-value">{formatCurrency(currentAmountDue)}</strong>
              </div>

              {/* Forma de pagamento */}
              <div className="pdv-payment-methods">
                {PAYMENT_METHODS.map(m => (
                  <button
                    key={m.value}
                    className={`pdv-method-btn ${paymentMethod === m.value ? 'active' : ''}`}
                    onClick={() => escolherForma(m.value)}
                    title={`Atalho: tecla ${m.tecla}`}
                  >
                    <span className="pdv-method-tecla" aria-hidden="true">{m.tecla}</span>
                    <span>{m.label}</span>
                  </button>
                ))}
              </div>

              {/* Valor recebido (apenas dinheiro) */}
              {paymentMethod === 'dinheiro' && (
                <div className="pdv-cash-section">
                  <label>Valor Recebido (R$)</label>
                  <input
                    type="number"
                    min={currentAmountDue}
                    step="0.01"
                    value={amountPaid}
                    onChange={e => setAmountPaid(e.target.value)}
                    className="pdv-cash-input"
                  />
                  {/* Atalhos de troco */}
                  <div className="pdv-cash-shortcuts">
                    <button
                      className="pdv-shortcut-btn pdv-shortcut-exato"
                      onClick={() => setAmountPaid(currentAmountDue.toFixed(2))}
                      title="Cliente entregou o valor exato"
                    >
                      Exato
                    </button>
                    {sugestoesDeTroco(currentAmountDue).map(v => (
                      <button key={v} className="pdv-shortcut-btn"
                        onClick={() => setAmountPaid(v.toFixed(2))}>
                        {formatCurrency(v)}
                      </button>
                    ))}
                  </div>
                  {parseFloat(amountPaid) >= currentAmountDue && (
                    <div className="pdv-change">
                      Troco: <strong>{formatCurrency(changeAmount)}</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Onda 69: cobrar e isentar deixaram de dividir a mesma linha.
                  Isentar zera a cobrança, e estava a um deslize de distância do
                  botão que o operador aperta dezenas de vezes por turno. Agora
                  o de cobrar ocupa a linha inteira, e o de isentar fica abaixo
                  da divisória, discreto, como a exceção que é. */}
              <div className="pdv-pay-actions">
                <button
                  className="pdv-btn-pay"
                  onClick={handlePayClick}
                  disabled={paying || (currentAmountDue === 0 && paymentMethod !== 'isento')}
                >
                  {paying ? 'Processando...' : `Confirmar ${formatCurrency(currentAmountDue)}`}
                  {!paying && <span className="pdv-btn-tecla" aria-hidden="true">Enter</span>}
                </button>
              </div>
              {podeIsentar && (
                <div className="pdv-excecoes">
                  <button className="pdv-btn-exempt" onClick={handleExempt}>
                    Isentar sem cobrar
                  </button>
                  <small>Exige justificativa e fica registrado na auditoria.</small>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
      {/* ─── Modal de Sangria / Suprimento ─────────────────────────────── */}
      {showMovementModal && (
        <div className="pdv-modal-overlay" onClick={() => setShowMovementModal(false)}>
          <div className="pdv-modal" onClick={e => e.stopPropagation()}>
            <div className="pdv-modal-header">
              <h2>{movementType === 'sangria' ? '↓ Sangria de Caixa' : '↑ Suprimento de Caixa'}</h2>
              <button className="pdv-modal-close" onClick={() => setShowMovementModal(false)}>✕</button>
            </div>
            <div className="pdv-modal-body">
              <p className="pdv-modal-desc">
                {movementType === 'sangria'
                  ? 'Registre uma retirada de dinheiro do caixa para segurança ou depósito.'
                  : 'Registre um reforço de troco ou adição de dinheiro ao caixa.'}
              </p>
              <div className="pdv-form-group">
                <label>Valor (R$) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0,00"
                  value={movementAmount}
                  onChange={e => setMovementAmount(e.target.value)}
                  className="pdv-input"
                  autoFocus
                />
              </div>
              <div className="pdv-form-group">
                <label>Motivo / Justificativa *</label>
                <input
                  type="text"
                  placeholder={movementType === 'sangria' ? 'Ex: Depósito bancário' : 'Ex: Reforço de troco'}
                  value={movementReason}
                  onChange={e => setMovementReason(e.target.value)}
                  className="pdv-input"
                  maxLength={255}
                />
              </div>
            </div>
            <div className="pdv-modal-footer">
              <button className="pdv-btn-secondary" onClick={() => setShowMovementModal(false)}>
                Cancelar
              </button>
              <button
                className={`pdv-btn-primary ${movementType}`}
                onClick={handleSaveMovement}
                disabled={savingMovement}
              >
                {savingMovement ? 'Salvando...' : `Confirmar ${movementType === 'sangria' ? 'Sangria' : 'Suprimento'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal de Fechamento de Caixa ───────────────────────────────── */}
      {showCloseModal && (
        <div className="pdv-modal-overlay">
          <div className="pdv-modal pdv-close-modal" onClick={e => e.stopPropagation()}>
            {!closingReport ? (
              <>
                <div className="pdv-modal-header">
                  <h2>Fechar Caixa — Sessão #{session?.id}</h2>
                  <button className="pdv-modal-close" onClick={() => setShowCloseModal(false)}>✕</button>
                </div>
                <div className="pdv-modal-body">
                  {/* Fechamento Cego: operador NÃO vê o saldo esperado antes de informar o físico */}
                  <div className="pdv-blind-close-info">
                    <p><strong>Conferência às cegas</strong></p>
                    <p>Conte o dinheiro que está na gaveta e informe o valor abaixo.
                    A comparação com o esperado aparece <em>depois</em> de confirmar —
                    é isso que distingue um caixa conferido de um caixa copiado da tela.</p>
                  </div>

                  {/* ─── Onda 75: o resumo estava induzindo ao erro ──────────
                      O rótulo era "Crédito + Débito + PIX + Sem Parar", a
                      fórmula em vez da informação, e o número aparecia logo
                      acima do campo que pede o valor contado. O William, que
                      conhece o sistema, perguntou se aqueles R$ 84,00 eram o
                      valor do caixa. Não eram: eram justamente o que NÃO está
                      na gaveta. Um operador teria contado errado.

                      Agora o rótulo diz o que o número é, e a linha avisa
                      explicitamente que ele não entra na conferência. */}
                  <div className="pdv-close-summary">
                    <div className="pdv-close-row tickets">
                      <span>Tickets atendidos no turno</span>
                      <strong>{session?.total_tickets}</strong>
                    </div>
                    <div className="pdv-close-row pdv-close-fora">
                      <span>
                        Recebido em cartão, PIX e Sem Parar
                        <small>não passa pela gaveta e não entra nesta conferência</small>
                      </span>
                      <strong>{formatCurrency((parseFloat(session?.total_credit) || 0) + (parseFloat(session?.total_debit) || 0) + (parseFloat(session?.total_pix) || 0) + (parseFloat(session?.total_sem_parar) || 0))}</strong>
                    </div>
                  </div>

                  {movements.length > 0 && (
                    <div className="pdv-movements-list">
                      <h4>Movimentações da Sessão</h4>
                      {movements.map(m => (
                        <div key={m.id} className={`pdv-movement-item ${m.type}`}>
                          <span className="pdv-movement-type">{m.type === 'sangria' ? '↓ Sangria' : '↑ Suprimento'}</span>
                          <span className="pdv-movement-amount">{formatCurrency(m.amount)}</span>
                          <span className="pdv-movement-reason">{m.reason}</span>
                          <span className="pdv-movement-time">{formatDateTime(m.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pdv-form-group">
                    {/* Onda 75: "Valor Físico Contado no Caixa" descrevia o
                        campo sem dizer o que contar. Só cédulas e moedas entram
                        aqui — cartão e PIX não passam pela gaveta. */}
                    <label>Quanto há em dinheiro na gaveta (R$) *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Some as cédulas e moedas"
                      value={closingBalance}
                      onChange={e => setClosingBalance(e.target.value)}
                      className="pdv-input pdv-input-highlight"
                      autoFocus
                    />
                    <small>
                      Conte antes de olhar o sistema. Só cédulas e moedas —
                      cartão e PIX não entram nesta conta.
                    </small>
                  </div>
                  <div className="pdv-form-group">
                    <label>Observações</label>
                    <input
                      type="text"
                      placeholder="Observações opcionais sobre o fechamento"
                      value={closingNotes}
                      onChange={e => setClosingNotes(e.target.value)}
                      className="pdv-input"
                      maxLength={255}
                    />
                  </div>
                </div>
                <div className="pdv-modal-footer">
                  <button className="pdv-btn-secondary" onClick={() => setShowCloseModal(false)}>
                    Cancelar
                  </button>
                  <button className="pdv-btn-danger" onClick={handleConfirmClose}>
                    Confirmar Fechamento
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="pdv-modal-header">
                  <h2>Caixa Fechado — Relatório Final</h2>
                </div>
                <div className="pdv-modal-body">
                  <div className="pdv-close-report">
                    <div className="pdv-report-section">
                      <h4>Resumo de Vendas</h4>
                      <div className="pdv-close-row"><span>Dinheiro</span><strong>{formatCurrency(closingReport.total_cash)}</strong></div>
                      <div className="pdv-close-row"><span>Cartão Crédito</span><strong>{formatCurrency(closingReport.total_credit)}</strong></div>
                      <div className="pdv-close-row"><span>Cartão Débito</span><strong>{formatCurrency(closingReport.total_debit)}</strong></div>
                      <div className="pdv-close-row"><span>PIX</span><strong>{formatCurrency(closingReport.total_pix)}</strong></div>
                      {parseFloat(closingReport.total_sem_parar || 0) > 0 && (
                        <div className="pdv-close-row sem-parar"><span>Sem Parar</span><strong>{formatCurrency(closingReport.total_sem_parar)}</strong></div>
                      )}
                      <div className="pdv-close-row total"><span>Total Geral</span><strong>{formatCurrency(closingReport.total_revenue)}</strong></div>
                    </div>
                    <div className="pdv-report-section">
                      <h4>Conferência de Caixa</h4>
                      {/* Onda 75: era "Saldo de Abertura" aqui e "Saldo Inicial"
                          na tela de abrir o caixa. O mesmo valor com dois nomes
                          faz o operador procurar uma terceira informação que não
                          existe. O nome que fica é o que ele digita ao abrir. */}
                      <div className="pdv-close-row"><span>Saldo Inicial</span><strong>{formatCurrency(closingReport.opening_balance)}</strong></div>
                      {closingReport.total_suprimento > 0 && <div className="pdv-close-row suprimento"><span>Suprimentos</span><strong>+ {formatCurrency(closingReport.total_suprimento)}</strong></div>}
                      {closingReport.total_sangria > 0 && <div className="pdv-close-row sangria"><span>Sangrias</span><strong>- {formatCurrency(closingReport.total_sangria)}</strong></div>}
                      <div className="pdv-close-row"><span>Saldo Esperado</span><strong>{formatCurrency(closingReport.expected_cash_balance)}</strong></div>
                      <div className="pdv-close-row"><span>Saldo Informado</span><strong>{formatCurrency(closingReport.closing_balance)}</strong></div>
                      <div className={`pdv-close-row divergence ${closingReport.has_divergence ? 'has-diff' : 'ok'}`}>
                        <span>Diferença</span>
                        <strong>{closingReport.cash_difference >= 0 ? '+' : ''}{formatCurrency(closingReport.cash_difference)}</strong>
                      </div>
                      {closingReport.has_divergence && (
                        <div className="pdv-divergence-alert">
                          Divergência detectada. Verifique o caixa e registre uma justificativa.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pdv-modal-footer">
                  <button className="pdv-btn-primary" onClick={handleFinishClose}>
                    Concluir e Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* ─── Modal de Confirmação Sem Parar ───────────────────────────────────── */}
      {showSemPararModal && (
        <div className="pdv-modal-overlay" onClick={() => setShowSemPararModal(false)}>
          <div className="pdv-modal pdv-semparar-modal" onClick={e => e.stopPropagation()}>
            <div className="pdv-modal-header">
              <h2>Pagamento Sem Parar</h2>
              <button className="pdv-modal-close" onClick={() => setShowSemPararModal(false)}>✕</button>
            </div>
            <div className="pdv-modal-body">
              <div className="pdv-semparar-info">
                
                <div className="pdv-semparar-plate">{ticketData?.ticket?.plate}</div>
                <div className="pdv-semparar-amount">{formatCurrency(currentAmountDue)}</div>
              </div>
              <div className="pdv-semparar-steps">
                <p className="pdv-semparar-instruction">
                  <strong>Confirme antes de prosseguir:</strong>
                </p>
                <ol>
                  <li>Verifique se o veículo possui tag Sem Parar ativa no para-brisa</li>
                  <li>Confirme que a placa <strong>{ticketData?.ticket?.plate}</strong> corresponde ao veículo presente</li>
                  <li>O valor de <strong>{formatCurrency(currentAmountDue)}</strong> será registrado como pago via Sem Parar</li>
                </ol>
                <div className="pdv-semparar-notice">
                  <em>Integração automática com a API Sem Parar pendente de credenciamento.<br/>
                  O pagamento é registrado manualmente pelo operador.</em>
                </div>
              </div>
            </div>
            <div className="pdv-modal-footer">
              <button className="pdv-btn-secondary" onClick={() => setShowSemPararModal(false)}>
                Cancelar
              </button>
              <button
                className="pdv-btn-pay pdv-btn-semparar"
                onClick={handleConfirmSemParar}
                disabled={paying}
              >
                {paying ? 'Processando...' : `✔ Confirmar Sem Parar — ${formatCurrency(currentAmountDue)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal de Evidência Fotográfica ─────────────────────────────── */}
      {evidenceModal && (
        <div
          className="pdv-evidence-modal-overlay"
          onClick={() => setEvidenceModal(null)}
        >
          <div className="pdv-evidence-modal" onClick={e => e.stopPropagation()}>
            <div className="pdv-evidence-modal-header">
              <span>Evidência Fotográfica</span>
              <button
                className="pdv-evidence-modal-close"
                onClick={() => setEvidenceModal(null)}
              >
                ✕
              </button>
            </div>
            <img
              src={evidenceModal}
              alt="Evidência de entrada"
              className="pdv-evidence-modal-img"
            />
          </div>
        </div>
      )}
    </>
  );
}
